<template>
  <div class="max-h-70-screen space-y-5 overflow-y-auto px-6 pb-6">
    <lf-card :has-padding="false" class="flex w-full space-x-4 p-6">
      <div class="flex w-1/2 flex-col space-y-8 bg-white">
        <div class="space-y-2">
          <lf-h3>{{ $t("OFFER_CUSTOMISATION.PRIMARY_OFFER_DETAILS") }}</lf-h3>
          <draggable
            :id="PRIMARY_FIELD_SECTION_ID"
            class="grid grid-cols-2 gap-6 pt-6"
            ghost-class="ghost"
            group="offer-fields"
            item-key="id"
            :list="primaryFields"
            :class="sectionDraggedOver ? 'cursor-grabbing' : 'cursor-grab'"
            :move="checkMove"
            @change="updateFields(BpOfferSection.PRIMARY, $event)"
            @start="sectionDraggedOver = BpOfferSection.PRIMARY"
            @end="sectionDraggedOver = null"
          >
            <template #item="{ element }: { element: OfferTemplateField }">
              <bp-offer-field
                :bp-offer-field="element"
                :show-dynamic-offer="showDynamicOffer"
                @update-dynamic-field="updateDynamicField"
              />
            </template>
            <template #footer>
              <drag-and-drop-placeholder
                v-if="sectionDraggedOver === BpOfferSection.EXPANDED"
                :disabled="isPrimaryFull"
                :class="{ 'col-span-2': isPrimaryFull }"
              />
            </template>
          </draggable>
        </div>
        <div>
          <lf-h3>{{ $t("OFFER_CUSTOMISATION.EXPANDED_OFFER_DETAILS") }}</lf-h3>
          <draggable
            :id="EXPANDED_FIELD_SECTION_ID"
            class="grid grid-cols-2 gap-6 pt-6"
            group="offer-fields"
            ghost-class="ghost"
            item-key="id"
            :list="expandedFields"
            :class="sectionDraggedOver ? 'cursor-grabbing' : 'cursor-grab'"
            :move="checkMove"
            @change="updateFields(BpOfferSection.EXPANDED, $event)"
            @start="sectionDraggedOver = BpOfferSection.EXPANDED"
            @end="sectionDraggedOver = null"
          >
            <template #item="{ element }: { element: OfferTemplateField }">
              <bp-offer-field
                :bp-offer-field="element"
                :show-dynamic-offer="showDynamicOffer"
                @update-dynamic-field="updateDynamicField"
              />
            </template>
            <template #footer>
              <drag-and-drop-placeholder
                v-if="sectionDraggedOver === BpOfferSection.PRIMARY"
              />
            </template>
          </draggable>
        </div>
      </div>
      <div class="w-1/2 bg-disabled p-2">
        <bp-offer-list
          :offers="offers"
          :show-dynamic-offer="showDynamicOffer"
          :dynamic-fields="dynamicFields"
        />
      </div>
    </lf-card>
  </div>
</template>
<script setup lang="ts">
import { type Ref, computed, inject, ref, watch } from "vue";
import draggable from "vuedraggable";

import xor from "lodash/xor";

import { BpOfferSection, OfferTemplateView } from "@/enums/offerTemplates";

import BpOfferList from "@/components/deals/workflow/BorrowerPlatform/BorrowerPlatformPreview/BorrowerPlatformOfferList.vue";
import BpOfferField from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/BpOfferField.vue";
import DragAndDropPlaceholder from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/DragAndDropPlaceholder.vue";

import type { IFunder } from "@/models/funders";
import type {
  OfferTemplate,
  OfferTemplateField,
  OfferView,
} from "@/models/offers";

const MAX_PRIMARY_FIELDS = 6;
const PRIMARY_FIELD_SECTION_ID = "primary-offer-fields";
const EXPANDED_FIELD_SECTION_ID = "expanded-offer-fields";

interface DraggableChangeEvent {
  added?: {
    element: OfferTemplateField;
    newIndex: number;
  };
  removed?: {
    element: OfferTemplateField;
    oldIndex: number;
  };
  moved?: {
    element: OfferTemplateField;
    newIndex: number;
    oldIndex: number;
  };
}

interface DraggableMoveEvent {
  draggedContext: {
    element: OfferTemplateField;
    index: number;
  };
  relatedContext: {
    element: OfferTemplateField;
    index: number;
  };
  from: HTMLElement;
  to: HTMLElement;
}

const emit = defineEmits<{
  "update:fields": [OfferTemplateField[]];
}>();

const { fields, template, funder } = defineProps<{
  fields: OfferTemplateField[];
  template: OfferTemplate;
  funder?: IFunder;
}>();

const dynamicFields = ref<string[]>([]);
const sectionDraggedOver = ref<BpOfferSection | null>(null);

const view = inject<Ref<OfferTemplateView>>(
  "offerTemplateView",
  ref(OfferTemplateView.BP_OFFER_DETAILS)
);

const showDynamicOffer = computed(
  () => view.value === OfferTemplateView.BP_OFFER_DETAILS
);

const isOfferAccepted = computed(
  () => view.value === OfferTemplateView.BP_CLOSING_DETAILS
);

const offers = computed<OfferView[]>(() => {
  const details = fields.map((field) => ({
    label: field[fieldCategory.value].label,
    name: field.name,
    section: field[fieldCategory.value].borrower_platform.section,
    type: field.type,
    value: "",
    weight: field[fieldCategory.value].borrower_platform.weight,
    options: field.options,
  }));

  return [
    {
      id: "1",
      product: template.name,
      product_description: template.description,
      funder: funder,
      accepted: isOfferAccepted.value,
      details: details,
    },
  ] as OfferView[];
});

const fieldCategory = computed(() =>
  view.value === OfferTemplateView.BP_OFFER_DETAILS
    ? OfferTemplateView.OFFER
    : OfferTemplateView.CLOSING
);

const primaryFields = ref<OfferTemplateField[]>([]);
const expandedFields = ref<OfferTemplateField[]>([]);

const updateFields = (section: BpOfferSection, event: DraggableChangeEvent) => {
  // Handle moving between sections
  if (event.added) {
    event.added.element[fieldCategory.value].borrower_platform = {
      ...event.added.element[fieldCategory.value].borrower_platform,
      section: section,
      weight: event.added.newIndex,
    };
  }

  // Update all fields in both sections to ensure correct weights
  primaryFields.value.forEach((field, index) => {
    field[fieldCategory.value].borrower_platform = {
      ...field[fieldCategory.value].borrower_platform,
      section: BpOfferSection.PRIMARY,
      weight: index,
    };
  });

  expandedFields.value.forEach((field, index) => {
    field[fieldCategory.value].borrower_platform = {
      ...field[fieldCategory.value].borrower_platform,
      section: BpOfferSection.EXPANDED,
      weight: index,
    };
  });

  emit("update:fields", [...primaryFields.value, ...expandedFields.value]);
};

const filterAndSortFields = (section: BpOfferSection) => {
  return fields
    .filter(
      (field) =>
        field[fieldCategory.value]?.borrower_platform?.section === section
    )
    .sort(
      (a, b) =>
        a[fieldCategory.value].borrower_platform?.weight -
        b[fieldCategory.value].borrower_platform?.weight
    );
};

const isPrimaryFull = computed(
  () => primaryFields.value.length >= MAX_PRIMARY_FIELDS
);

const checkMove = (event: DraggableMoveEvent) => {
  const fromId = event.from.id;
  const toId = event.to.id;
  if (fromId === toId) {
    return true;
  }

  return !(toId === PRIMARY_FIELD_SECTION_ID && isPrimaryFull.value);
};

const updateDynamicField = (fieldId: string) => {
  const field = fields.find(({ id }) => id === fieldId);
  if (!field) {
    return;
  }
  field.dynamic = !field.dynamic;
  dynamicFields.value = xor(dynamicFields.value, [field.name]);
  emit("update:fields", [field]);
};

watch(
  () => fields,
  () => {
    primaryFields.value = filterAndSortFields(BpOfferSection.PRIMARY);
    expandedFields.value = filterAndSortFields(BpOfferSection.EXPANDED);
    dynamicFields.value = fields
      .filter(({ dynamic }) => dynamic)
      .flatMap(({ name }) => name);
  },
  { deep: true, immediate: true }
);
</script>
<style>
@reference "@/assets/tailwind.css";

.ghost {
  @apply border-2 border-dashed border-gray-200;
}
</style>
